import { ServiceLocator } from './../../../services/infrastructure/service-locator.service';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { ELoadingIndicatorStyle } from 'projects/shared/lib/enum/loading/loading-indicator-style.enum';
import { ENVIRONMENT } from 'projects/shared/lib/environment/environment';
import { INavigationEnvironment } from 'projects/shared/lib/interfaces/environment/navigation/navigation-environment.interface';
import { EError, EShoppingCartGetQueryType } from 'projects/shared/lib/services/INTEGRA_Shop_Client';
import { ICartService, ICartServiceToken } from 'projects/shared/lib/services/data/cart/cart-service.interface';
import { IUserService, IUserServiceToken } from 'projects/shared/lib/services/data/user/user-service.interface';
import { IAuthService, IAuthServiceToken } from 'projects/shared/lib/services/infrastructure/authentication/auth-service.interface';
import { firstValueFrom, tap } from 'rxjs';
import { ToastItem } from '../../shared/toast/item/toast-item.model';
import { EToastItemType } from 'projects/shared/lib/enum/toast/toast-entry-type.enum';
import { ErrorCodeService } from 'projects/shared/lib/services/infrastructure/error-code.service';
import { IIdpApiService, IIdpApiServiceToken } from 'projects/shared/lib/services/api/idp/idp-api-service.interface';
import { IConfigurationServiceToken } from 'projects/shared/lib/services/infrastructure/configuration/configuration-service.interface';
import { IToastService, IToastServiceToken } from 'shared/services/ui/toast/toast-service.interface';

@Component({
  selector: 'app-login-as',
  templateUrl: './login-as-base.component.html',
  standalone: false
})
export class LoginAsBaseComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly authService: IAuthService = inject(IAuthServiceToken);
  private readonly cartService: ICartService = inject(ICartServiceToken);
  private readonly toastService: IToastService = inject(IToastServiceToken);
  private readonly userService: IUserService = inject(IUserServiceToken);
  protected readonly idpApiService: IIdpApiService | null = inject(IIdpApiServiceToken, { optional: true });

  private router: Router;
  private accessToken: string | undefined;
  private authorizedLandingPage: INavigationEnvironment;
  private errorLandingPage: string;
  public eLoadingIndicatorStyle: ELoadingIndicatorStyle;
  private loginRequiresCookieAcceptance: boolean;

  public constructor() {
    this.router = ServiceLocator.injector.get(Router);

    this.errorLandingPage = ENVIRONMENT.errorLandingPage;
    this.eLoadingIndicatorStyle = ELoadingIndicatorStyle.OVERLAY;
    this.authorizedLandingPage = ENVIRONMENT.authorizedLandingPage;
    this.loginRequiresCookieAcceptance = ENVIRONMENT.auth.loginRequiresCookiesAcceptance;
    ServiceLocator.injector.get(IConfigurationServiceToken).authorizedLandingPageGet().subscribe((targetPage) => {
      this.authorizedLandingPage.routerlink = targetPage.routerlink as string;
      let queryParams = new URLSearchParams(targetPage.queryParams);
      let queryParamsObject: Params = {};
      queryParams.forEach((value, key) => {
        queryParamsObject[key] = value;
      });
      this.authorizedLandingPage.queryParams = queryParamsObject;
    });
  }

  public ngOnInit(): void {
    this.authSubscriptionInit();
    this.idpApiService?.logout();

    firstValueFrom(this.userService.userLogout())
      .then(() => this.authService.accessJWTClear())
      .then(() => {
        if (this.loginRequiresCookieAcceptance) {
          this.authService.cookieTermsAcceptanceStateSet('yes');
        }
      })
      .then(async () => await this.loginAsMitbenAsync());
  }

  private authSubscriptionInit() {
    this.authService.authenticatedGet().pipe(
      takeUntilDestroyed(this.destroyRef),
      tap(
        (isAuthenticated) => {
          if (isAuthenticated == true) {
            this.router.navigate([this.authorizedLandingPage.routerlink], { queryParams: this.authorizedLandingPage.queryParams });
          }
        }
      )
    ).subscribe();
  }

  private async loginAsMitbenAsync() {
    let keyValue = ServiceLocator.injector.get(ActivatedRoute).snapshot.queryParamMap.get("key");
    if (keyValue != undefined) {
      await firstValueFrom(this.userService.loginAsMitben(keyValue)).then((accessToken) => {
        this.accessToken = accessToken;

        if (this.accessToken == undefined) {
          this.toastService.toastItemAdd(new ToastItem({
            type: EToastItemType.Danger,
            title: $localize`:@@LoginAsBaseLoginAsMitbenTokenEmptyErrorHeading:Failure`,
            message: $localize`:@@LoginAsBaseLoginAsMitbenTokenEmptyErrorMessage:Login failed. No JWT-Token available for your link.`,
            code: ErrorCodeService.formatErrorCode(EError.F200150),
          }));
          return;
        }

        let isLoginSuccessfull = this.authService.isAccessJWTValid(this.accessToken);
        if (isLoginSuccessfull != true) {
          this.toastService.toastItemAdd(new ToastItem({
            type: EToastItemType.Danger,
            title: $localize`:@@LoginAsBaseLoginAsMitbenTokenNotValidErrorHeading:Failure`,
            message: $localize`:@@LoginAsBaseLoginAsMitbenTokenNotValidErrorMessage:Login failed. JWT validation failed.`,
            code: ErrorCodeService.formatErrorCode(EError.F200160),
          }));
          return;
        }

        this.authService.accessJWTSet(this.accessToken);

        setTimeout(() => {
          firstValueFrom(this.userService.userFavoritesInitialize());
          this.cartService.cartCurrentInitAsync(EShoppingCartGetQueryType.GetLatest);
        });

        this.router.navigate([this.authorizedLandingPage.routerlink], { queryParams: this.authorizedLandingPage.queryParams });
      }).catch((error) => {
        this.toastService.toastItemAdd(new ToastItem({
          type: EToastItemType.Danger,
          title: $localize`:@@LoginAsBaseLoginAsMitbenErrorHeading:Failure`,
          message: $localize`:@@LoginAsBaseLoginAsMitbenErrorMessage:Could not login for the user, please try again.`,
          code: JSON.parse(error.response).errorCode
        }));
      });
    }
  }

}
